<template>
    <v-container class="py-12 recurrence-monitor">
        <!-- TÍTULO -->
        <v-row>
            <v-col cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span class="page-title">Monitoramento de recorrências</span>
                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="[
                            { text: 'Home', to: '/home' },
                            { text: 'Recorrências', disabled: true }
                        ]" />
                    </div>
                    <div class="button-group" style="padding-right: 16px;">
                        <v-btn outlined color="primary" elevation="0" :loading="summaryLoading || dataList.loading"
                            @click="reload">
                            <v-icon left small>mdi-refresh</v-icon>Atualizar
                        </v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>

        <!-- INDICADORES -->
        <div class="stat-grid mb-1">
            <stat-card v-for="kpi in kpis" :key="kpi.key" :label="kpi.label" :icon="kpi.icon"
                :value="summaryLoading && summary === null ? '…' : kpi.value" :hint="kpi.hint"
                :clickable="!!kpi.filter" :alert="kpi.key === 'alerts' && !!kpi.value" @click="kpi.filter && kpi.filter()" />
        </div>

        <!-- FILTRO -->
        <v-row>
            <v-col cols="12">
                <v-card class="filter-card">
                    <v-card-title class="filter-card-title">Filtros de busca</v-card-title>
                    <v-card-text>
                        <v-form @submit.prevent="applyFilters">
                            <v-row dense>
                                <v-col cols="12" md="3">
                                    <v-autocomplete dense outlined hide-details clearable label="Empresa"
                                        :items="customerItems" item-value="id" item-text="name"
                                        :loading="customerLoading" :search-input.sync="customerSearch"
                                        :filter="() => true" v-model="dataList.filter.customer"
                                        no-data-text="Nenhuma empresa encontrada" @change="onCustomerChange" />
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-autocomplete dense outlined hide-details clearable label="Solicitante"
                                        :items="requesterItems" item-value="id" item-text="name"
                                        :loading="requesterLoading" :search-input.sync="requesterSearch"
                                        :filter="() => true" v-model="dataList.filter.requester"
                                        no-data-text="Nenhum solicitante encontrado" @change="onRequesterChange" />
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-text-field dense outlined hide-details label="Nome da recorrência"
                                        prepend-inner-icon="mdi-magnify" v-model="dataList.filter.name" />
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-text-field dense outlined hide-details type="date" label="Início a partir de"
                                        v-model="dataList.filter.startDate" />
                                </v-col>
                            </v-row>
                            <div class="status-filter mt-3">
                                <span class="status-filter__label">Situação</span>
                                <v-chip-group v-model="dataList.filter.status" column @change="applyFilters">
                                    <v-chip v-for="status in statusOptions" :key="String(status.id)" :value="status.id"
                                        filter outlined small :color="status.color">
                                        {{ status.name }}
                                    </v-chip>
                                </v-chip-group>
                                <v-checkbox v-model="dataList.filter.alert" dense hide-details class="mt-0 ml-4"
                                    label="Somente com alerta" @change="applyFilters" />
                            </div>
                            <div class="mt-3">
                                <v-btn color="primary" elevation="0" type="submit">Aplicar filtros</v-btn>
                                <v-btn text class="ml-2" :disabled="!hasFilters" @click="clearFilters">Limpar</v-btn>
                            </div>
                        </v-form>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- LISTA -->
        <v-row>
            <v-col cols="12">
                <div class="list-heading">
                    <span>{{ resultDescription }}</span>
                    <v-progress-circular v-if="dataList.loading" indeterminate color="primary" size="20" width="2" />
                </div>

                <v-card v-if="!dataList.loading && !dataList.data.length" outlined class="empty-card">
                    <v-icon size="48" color="grey lighten-1">mdi-calendar-remove-outline</v-icon>
                    <p>Nenhuma recorrência encontrada.</p>
                </v-card>

                <v-card v-for="item in dataList.data" :key="item.id" outlined class="rec-card"
                    :class="`rec-card--status-${item.status}`">
                    <div class="rec-card__header">
                        <div>
                            <span class="rec-card__eyebrow">RECORRÊNCIA #{{ item.id }}</span>
                            <h3>{{ item.name || 'Recorrência sem nome' }}</h3>
                            <span class="rec-card__owner">
                                <v-icon x-small>mdi-domain</v-icon> {{ customerName(item) }}
                                <span class="mx-1">·</span>
                                <v-icon x-small>mdi-account-tie-outline</v-icon> {{ item.requester ? item.requester.name : 'Solicitante não informado' }}
                            </span>
                        </div>
                        <div class="text-right">
                            <v-chip small dark :color="statusColor(item.status)">{{ statusName(item.status) }}</v-chip>
                            <div v-if="item.generates_today" class="rec-card__today">
                                <v-icon x-small color="primary">mdi-calendar-today</v-icon> Gera hoje
                            </div>
                        </div>
                    </div>

                    <v-alert v-for="(alert, index) in item.alerts" :key="`alert-${item.id}-${index}`" dense text
                        type="warning" class="rec-card__alert body-2">{{ alert }}</v-alert>

                    <div class="rec-card__grid">
                        <div class="rec-info">
                            <small>COMO SE REPETE</small>
                            <strong>{{ recurrenceDescription(item) }}</strong>
                            <span v-if="item.base_time">Horário base: {{ item.base_time }}</span>
                        </div>
                        <div class="rec-info">
                            <small>PERÍODO</small>
                            <strong>{{ periodDescription(item) }}</strong>
                            <v-progress-linear :value="periodPercent(item)" height="5" rounded color="primary"
                                background-color="grey lighten-3" class="my-1" />
                            <span>{{ periodProgress(item) }}</span>
                        </div>
                        <div class="rec-info">
                            <small>PASSAGEIROS</small>
                            <strong>{{ item.passenger_count || 0 }} passageiro(s)</strong>
                            <span>{{ passengerNames(item) }}</span>
                        </div>
                        <div class="rec-info">
                            <small>PRÓXIMA GERAÇÃO</small>
                            <strong>{{ nextLabel(item) }}</strong>
                            <span>Última: {{ item.last_generated_date ? formatDate(item.last_generated_date) : 'nenhuma' }}
                                <template v-if="item.last_run"> · {{ runLabel(item.last_run) }}</template>
                            </span>
                        </div>
                        <div class="rec-info">
                            <small>SOLICITAÇÕES GERADAS</small>
                            <strong>{{ item.request_stats ? item.request_stats.total : 0 }}</strong>
                            <span class="rec-stats" v-if="item.request_stats">
                                <span title="Pendentes">⏳ {{ item.request_stats.pending }}</span>
                                <span title="Aprovadas">✔ {{ item.request_stats.approved }}</span>
                                <span title="Viraram OS">🚐 {{ item.request_stats.finished }}</span>
                                <span title="Rejeitadas">✖ {{ item.request_stats.rejected }}</span>
                            </span>
                        </div>
                    </div>

                    <div class="rec-card__footer">
                        <v-btn text color="primary" @click="openDetails(item)">
                            <v-icon left>mdi-eye-outline</v-icon>Ver detalhes
                        </v-btn>
                        <v-spacer />
                        <v-btn v-if="canUpdate && item.status === 1 && item.generates_today" small outlined color="primary"
                            class="mr-2" :loading="generatingId === item.id" @click="generateNow(item)">
                            <v-icon left small>mdi-flash-outline</v-icon>Gerar agora
                        </v-btn>
                        <v-btn v-if="canUpdate && item.status === 2" small outlined color="success" class="mr-2"
                            :loading="updatingId === item.id" @click="updateStatus(item, 1)">
                            <v-icon left small>mdi-play-outline</v-icon>Retomar
                        </v-btn>
                        <v-btn v-if="canUpdate && item.status === 1" small outlined color="orange darken-2" class="mr-2"
                            :loading="updatingId === item.id" @click="updateStatus(item, 2)">
                            <v-icon left small>mdi-pause</v-icon>Pausar
                        </v-btn>
                        <v-btn v-if="canUpdate" small text color="primary" class="mr-2" @click="goToEdit(item.id)">
                            <v-icon left small>mdi-pencil-outline</v-icon>Editar
                        </v-btn>
                        <v-btn v-if="canUpdate && (item.status === 1 || item.status === 2)" small text color="error"
                            @click="cancelRecurrence(item)">Encerrar</v-btn>
                    </div>
                </v-card>

                <div v-if="pageCount > 1" class="d-flex justify-center mt-4">
                    <v-pagination v-model="dataList.page" :length="pageCount" :total-visible="7" @input="load" />
                </div>
            </v-col>
        </v-row>

        <!-- DETALHES -->
        <v-dialog v-model="details.open" max-width="980px" scrollable>
            <v-card v-if="details.item">
                <v-card-title class="custom-modal-title text-uppercase black--text font-weight-bold">
                    Recorrência #{{ details.item.id }} · {{ details.item.name }}
                    <v-spacer />
                    <v-chip small dark :color="statusColor(details.item.status)" class="mr-2">
                        {{ statusName(details.item.status) }}
                    </v-chip>
                    <v-btn icon @click="details.open = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>

                <v-tabs v-model="details.tab" class="px-4">
                    <v-tab>Programação</v-tab>
                    <v-tab @click="loadRequests(1)">Histórico</v-tab>
                    <v-tab @click="loadRuns(1)">Execuções</v-tab>
                </v-tabs>

                <v-card-text class="details-body">
                    <v-tabs-items v-model="details.tab">
                        <!-- PROGRAMAÇÃO -->
                        <v-tab-item>
                            <div class="details-grid">
                                <div><small>Como se repete</small><strong>{{ recurrenceDescription(details.item) }}</strong></div>
                                <div><small>Período</small><strong>{{ periodDescription(details.item) }}</strong></div>
                                <div><small>Horário base</small><strong>{{ details.item.base_time || 'Não informado' }}</strong></div>
                                <div><small>Empresa</small><strong>{{ customerName(details.item) }}</strong></div>
                                <div><small>Solicitante</small><strong>{{ details.item.requester ? details.item.requester.name : '-' }}</strong></div>
                                <div><small>Solicitações geradas</small><strong>{{ details.item.request_stats ? details.item.request_stats.total : 0 }}</strong></div>
                            </div>

                            <h4 class="section-title">Próximas gerações</h4>
                            <div v-if="details.item.next_dates && details.item.next_dates.length" class="next-dates">
                                <div v-for="date in details.item.next_dates" :key="date" class="next-date">
                                    <span>{{ formatDay(date) }}</span>
                                    <div><strong>{{ formatWeekday(date) }}</strong><small>{{ formatMonth(date) }}</small></div>
                                </div>
                            </div>
                            <p v-else class="grey--text body-2">
                                {{ details.item.status === 1 ? 'Nenhuma data prevista dentro do período.' : 'Recorrência não está ativa.' }}
                            </p>

                            <h4 class="section-title">Passageiros ({{ details.item.passenger_count || 0 }})</h4>
                            <v-simple-table dense>
                                <tbody>
                                    <tr v-for="passenger in details.item.passengers" :key="`p-${passenger.id}`">
                                        <td>{{ passenger.name }}</td>
                                        <td class="grey--text">{{ passenger.registration || '' }}</td>
                                        <td class="grey--text">{{ passenger.costCenter ? (passenger.costCenter.name || passenger.costCenter.code) : '' }}</td>
                                    </tr>
                                </tbody>
                            </v-simple-table>
                        </v-tab-item>

                        <!-- HISTÓRICO -->
                        <v-tab-item>
                            <v-data-table dense :headers="requestHeaders" :items="details.requests.data"
                                :loading="details.requests.loading" :server-items-length="details.requests.total"
                                :options.sync="details.requests.options" @update:page="loadRequests"
                                :footer-props="{ itemsPerPageOptions: [20] }" no-data-text="Nenhuma solicitação gerada">
                                <template v-slot:[`item.date`]="{ item }">{{ formatDate(item.date) }} {{ item.time || '' }}</template>
                                <template v-slot:[`item.passenger`]="{ item }">{{ item.passenger ? item.passenger.name : '-' }}</template>
                                <template v-slot:[`item.status`]="{ item }">
                                    <v-chip x-small dark :color="requestStatusColor(item.status)">{{ requestStatusName(item.status) }}</v-chip>
                                </template>
                                <template v-slot:[`item.trip_id`]="{ item }">
                                    <span v-if="item.trip_id" class="primary--text font-weight-bold">OS #{{ item.trip_id }}</span>
                                    <span v-else class="grey--text">-</span>
                                </template>
                            </v-data-table>
                        </v-tab-item>

                        <!-- EXECUÇÕES -->
                        <v-tab-item>
                            <v-data-table dense :headers="runHeaders" :items="details.runs.data"
                                :loading="details.runs.loading" :server-items-length="details.runs.total"
                                :options.sync="details.runs.options" @update:page="loadRuns"
                                :footer-props="{ itemsPerPageOptions: [20] }" no-data-text="Nenhuma execução registrada">
                                <template v-slot:[`item.run_date`]="{ item }">{{ formatDate(item.run_date) }}</template>
                                <template v-slot:[`item.source`]="{ item }">
                                    {{ item.source === 'manual' ? `Manual${item.user_name ? ' - ' + item.user_name : ''}` : 'Automática' }}
                                </template>
                                <template v-slot:[`item.failed_count`]="{ item }">
                                    <span :class="{ 'error--text font-weight-bold': item.failed_count > 0 }">{{ item.failed_count }}</span>
                                </template>
                                <template v-slot:[`item.message`]="{ item }">
                                    <span class="body-2">{{ item.message || '-' }}</span>
                                </template>
                            </v-data-table>
                        </v-tab-item>
                    </v-tabs-items>
                </v-card-text>

                <v-card-actions>
                    <v-spacer />
                    <v-btn v-if="canUpdate && details.item.status === 1 && details.item.generates_today" outlined
                        color="primary" :loading="generatingId === details.item.id" @click="generateNow(details.item)">
                        <v-icon left small>mdi-flash-outline</v-icon>Gerar agora
                    </v-btn>
                    <v-btn text @click="details.open = false">Fechar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from "axios";
import moment from "moment";
import "moment/locale/pt-br";
import debounce from "lodash/debounce";

const STATUS = {
    1: { name: "Ativa", color: "green" },
    2: { name: "Pausada", color: "orange darken-2" },
    3: { name: "Cancelada", color: "red" },
    4: { name: "Finalizada", color: "blue-grey" }
};

const REQUEST_STATUS = {
    1: { name: "Pendente", color: "grey" },
    2: { name: "Aprovada", color: "blue" },
    3: { name: "Rejeitada", color: "red" },
    4: { name: "Virou OS", color: "green" }
};

const WEEKDAY_NAMES = { Dom: "domingos", Seg: "segundas", Ter: "terças", Qua: "quartas", Qui: "quintas", Sex: "sextas", "Sáb": "sábados" };

const emptyFilter = () => ({ customer: null, requester: null, name: "", startDate: "", status: null, alert: false });

export default {
    name: "TripRecurrenceList",

    data: () => ({
        summary: null,
        summaryLoading: false,
        dataList: {
            filter: emptyFilter(),
            data: [],
            total: 0,
            page: 1,
            limit: 10,
            loading: false
        },
        statusOptions: [
            { id: 1, name: "Ativas", color: "green" },
            { id: 2, name: "Pausadas", color: "orange darken-2" },
            { id: 3, name: "Canceladas", color: "red" },
            { id: 4, name: "Finalizadas", color: "blue-grey" }
        ],
        customers: [],
        selectedCustomer: null,
        customerSearch: null,
        customerLoading: false,
        requesters: [],
        selectedRequester: null,
        requesterSearch: null,
        requesterLoading: false,
        updatingId: null,
        generatingId: null,
        details: {
            open: false,
            tab: 0,
            item: null,
            requests: { data: [], total: 0, loading: false, options: { page: 1, itemsPerPage: 20 } },
            runs: { data: [], total: 0, loading: false, options: { page: 1, itemsPerPage: 20 } }
        },
        requestHeaders: [
            { text: "Data", value: "date", sortable: false },
            { text: "Passageiro", value: "passenger", sortable: false },
            { text: "Situação", value: "status", sortable: false },
            { text: "OS", value: "trip_id", sortable: false }
        ],
        runHeaders: [
            { text: "Data", value: "run_date", sortable: false, width: 110 },
            { text: "Origem", value: "source", sortable: false, width: 170 },
            { text: "Criadas", value: "created_count", sortable: false, width: 80 },
            { text: "Falhas", value: "failed_count", sortable: false, width: 80 },
            { text: "Detalhes", value: "message", sortable: false }
        ]
    }),

    computed: {
        canUpdate() {
            return this.$check(["recurrences.update"]);
        },
        pageCount() {
            return Math.max(Math.ceil(this.dataList.total / this.dataList.limit), 1);
        },
        hasFilters() {
            const f = this.dataList.filter;
            return !!(f.customer || f.requester || f.name || f.startDate || f.status || f.alert);
        },
        resultDescription() {
            if (this.dataList.loading && !this.dataList.data.length) return "Carregando recorrências...";
            const total = this.dataList.total;
            return `${total} ${total === 1 ? "recorrência encontrada" : "recorrências encontradas"}`;
        },
        customerItems() {
            const list = [...this.customers];
            if (this.selectedCustomer && !list.some(item => item.id === this.selectedCustomer.id)) list.unshift(this.selectedCustomer);
            return list;
        },
        requesterItems() {
            const list = [...this.requesters];
            if (this.selectedRequester && !list.some(item => item.id === this.selectedRequester.id)) list.unshift(this.selectedRequester);
            return list;
        },
        kpis() {
            const s = this.summary || {};
            return [
                { key: "active", label: "Ativas", value: s.active || 0, icon: "mdi-calendar-sync", color: "#2e7d32", filter: () => this.quickStatus(1) },
                { key: "paused", label: "Pausadas", value: s.paused || 0, icon: "mdi-pause-circle-outline", color: "#ef6c00", filter: () => this.quickStatus(2) },
                { key: "finished", label: "Finalizadas", value: s.finished || 0, icon: "mdi-calendar-check", color: "#546e7a", filter: () => this.quickStatus(4) },
                { key: "cancelled", label: "Canceladas", value: s.cancelled || 0, icon: "mdi-calendar-remove", color: "#c62828", filter: () => this.quickStatus(3) },
                {
                    key: "today", label: "Geram hoje", value: s.generating_today || 0, icon: "mdi-calendar-today", color: "#1450b5",
                    hint: s.generating_today ? `${s.generated_requests_today || 0} de ${s.expected_requests_today || 0} solicitações criadas` : null
                },
                { key: "alerts", label: "Precisam de atenção", value: s.alerts || 0, icon: "mdi-alert-outline", color: "#e65100", filter: () => this.quickAlert() }
            ];
        }
    },

    watch: {
        customerSearch(value) {
            this.searchCustomers(value || "");
        },
        requesterSearch(value) {
            this.searchRequesters(value || "");
        }
    },

    created() {
        moment.locale("pt-br");
        this.reload();
        this.searchCustomers("");
        this.searchRequesters("");
    },

    methods: {
        searchPayload() {
            const f = this.dataList.filter;
            return {
                customer: f.customer || undefined,
                requester: f.requester || undefined,
                name: f.name || undefined,
                startDate: f.startDate || undefined,
                status: f.status || undefined,
                alert: f.alert ? 1 : undefined
            };
        },

        reload() {
            this.loadSummary();
            this.load();
        },

        async loadSummary() {
            this.summaryLoading = true;
            try {
                const { customer, requester } = this.searchPayload();
                const response = await axios.get("/api/v2/trip-recurrences/summary", {
                    params: { search: JSON.stringify({ customer, requester }) }
                });
                this.summary = response.data.content;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 4000 });
            } finally {
                this.summaryLoading = false;
            }
        },

        async load() {
            this.dataList.loading = true;
            try {
                const response = await axios.get("/api/v2/trip-recurrences", {
                    params: { page: this.dataList.page, limit: this.dataList.limit, search: JSON.stringify(this.searchPayload()) }
                });
                const content = response.data.content;
                this.dataList.data = content.data || [];
                this.dataList.total = content.total || 0;

                // Mantém o detalhe aberto atualizado
                if (this.details.open && this.details.item) {
                    const fresh = this.dataList.data.find(item => item.id === this.details.item.id);
                    if (fresh) this.details.item = fresh;
                }
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 4000 });
            } finally {
                this.dataList.loading = false;
            }
        },

        applyFilters() {
            this.dataList.page = 1;
            this.reload();
        },

        clearFilters() {
            this.dataList.filter = emptyFilter();
            this.selectedCustomer = null;
            this.selectedRequester = null;
            this.applyFilters();
        },

        quickStatus(status) {
            this.dataList.filter.status = status;
            this.dataList.filter.alert = false;
            this.applyFilters();
        },

        quickAlert() {
            this.dataList.filter.status = null;
            this.dataList.filter.alert = true;
            this.applyFilters();
        },

        searchCustomers: debounce(async function (name) {
            this.customerLoading = true;
            try {
                const response = await axios.get("/api/v2/customers", { params: { page: 1, limit: 20, search: { name } } });
                const content = response.data.content;
                this.customers = content.data || content || [];
            } catch (error) {
                console.error("Erro ao buscar empresas:", error);
            } finally {
                this.customerLoading = false;
            }
        }, 400),

        searchRequesters: debounce(async function (name) {
            this.requesterLoading = true;
            try {
                const response = await axios.get("/api/v2/requesters", { params: { page: 1, limit: 20, search: JSON.stringify({ name }) } });
                const content = response.data.content;
                this.requesters = content.data || content || [];
            } catch (error) {
                console.error("Erro ao buscar solicitantes:", error);
            } finally {
                this.requesterLoading = false;
            }
        }, 400),

        onCustomerChange(id) {
            this.selectedCustomer = this.customerItems.find(item => item.id === id) || null;
            this.applyFilters();
        },

        onRequesterChange(id) {
            this.selectedRequester = this.requesterItems.find(item => item.id === id) || null;
            this.applyFilters();
        },

        // ----- Ações -----

        async updateStatus(item, status) {
            this.updatingId = item.id;
            try {
                await axios.put(`/api/v2/trip-recurrences/${item.id}/status`, { status });
                this.$dialog.notify.success(status === 1 ? "Recorrência retomada" : "Recorrência pausada", { position: "top-right", timeout: 3000 });
                this.reload();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
            } finally {
                this.updatingId = null;
            }
        },

        async cancelRecurrence(item) {
            const confirmed = await this.$dialog.confirm({
                title: "Encerrar recorrência",
                text: `Encerrar <strong>${item.name || "a recorrência #" + item.id}</strong>? Nenhuma nova solicitação será gerada.`,
                actions: { false: "Cancelar", true: "Encerrar" }
            });
            if (!confirmed) return;

            try {
                await axios.put(`/api/v2/trip-recurrences/${item.id}/cancel`);
                this.$dialog.notify.success("Recorrência encerrada", { position: "top-right", timeout: 3000 });
                this.reload();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
            }
        },

        async generateNow(item) {
            const confirmed = await this.$dialog.confirm({
                title: "Gerar agora",
                text: `Gerar agora as solicitações de hoje da recorrência <strong>${item.name || "#" + item.id}</strong>? Passageiros que já têm solicitação hoje são ignorados.`,
                actions: { false: "Cancelar", true: "Gerar" }
            });
            if (!confirmed) return;

            this.generatingId = item.id;
            try {
                const response = await axios.post(`/api/v2/trip-recurrences/${item.id}/generate`);
                const result = response.data.content;
                const skipped = (result.skipped || []).length;
                const failed = result.failed || [];

                if (failed.length) {
                    this.$dialog.notify.warning(
                        `${result.created} criada(s), ${failed.length} com falha: ${failed.map(f => (f.passenger ? f.passenger + ": " : "") + f.reason).join("; ")}`,
                        { position: "top-right", timeout: 9000 }
                    );
                } else {
                    this.$dialog.notify.success(
                        `${result.created} solicitação(ões) criada(s)${skipped ? `, ${skipped} já existia(m)` : ""}`,
                        { position: "top-right", timeout: 5000 }
                    );
                }

                this.reload();
                if (this.details.open && this.details.tab === 2) this.loadRuns(1);
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 6000 });
            } finally {
                this.generatingId = null;
            }
        },

        goToEdit(id) {
            this.$router.push({ name: "TripRecurrenceDetails", params: { id } });
        },

        // ----- Detalhes -----

        openDetails(item) {
            this.details.item = item;
            this.details.tab = 0;
            this.details.requests = { data: [], total: 0, loading: false, options: { page: 1, itemsPerPage: 20 } };
            this.details.runs = { data: [], total: 0, loading: false, options: { page: 1, itemsPerPage: 20 } };
            this.details.open = true;
        },

        async loadRequests(page = 1) {
            if (!this.details.item) return;
            const target = this.details.requests;
            target.loading = true;
            try {
                const response = await axios.get(`/api/v2/trip-recurrences/${this.details.item.id}/requests`, { params: { page, limit: 20 } });
                const content = response.data.content;
                target.data = content.data || [];
                target.total = content.total || 0;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 4000 });
            } finally {
                target.loading = false;
            }
        },

        async loadRuns(page = 1) {
            if (!this.details.item) return;
            const target = this.details.runs;
            target.loading = true;
            try {
                const response = await axios.get(`/api/v2/trip-recurrences/${this.details.item.id}/runs`, { params: { page, limit: 20 } });
                const content = response.data.content;
                target.data = content.data || [];
                target.total = content.total || 0;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 4000 });
            } finally {
                target.loading = false;
            }
        },

        // ----- Formatação -----

        statusName(status) {
            return (STATUS[status] || {}).name || "Não informada";
        },
        statusColor(status) {
            return (STATUS[status] || {}).color || "grey";
        },
        requestStatusName(status) {
            return (REQUEST_STATUS[status] || {}).name || status;
        },
        requestStatusColor(status) {
            return (REQUEST_STATUS[status] || {}).color || "grey";
        },
        customerName(item) {
            const customers = item.requester && item.requester.customers;
            if (customers && customers.length) return customers.map(c => c.name).join(", ");
            const passenger = (item.passengers || []).find(p => p.customer);
            return passenger ? passenger.customer.name : "Empresa não informada";
        },
        joinWithAnd(values) {
            if (values.length <= 1) return values.join("");
            return `${values.slice(0, -1).join(", ")} e ${values[values.length - 1]}`;
        },
        recurrenceDescription(item) {
            const config = item.config || {};
            const every = Number(config.interval) > 1 ? Number(config.interval) : 1;

            if (config.type === "daily") return every > 1 ? `A cada ${every} dias` : "Todos os dias";
            if (config.type === "weekly") {
                const days = this.joinWithAnd((config.weekdays || []).map(day => WEEKDAY_NAMES[day] || day));
                const prefix = every > 1 ? `A cada ${every} semanas` : "Toda semana";
                return days ? `${prefix}, às ${days}` : prefix;
            }
            if (config.type === "monthly") {
                const days = this.joinWithAnd((config.monthDays || []).map(day => `dia ${day}`));
                const prefix = every > 1 ? `A cada ${every} meses` : "Todo mês";
                return days ? `${prefix}, no ${days}` : prefix;
            }
            if (config.type === "fixedDates") {
                const count = (config.fixedDates || []).length;
                return `${count} data(s) específica(s)`;
            }
            return "Configuração não informada";
        },
        startOf(item) {
            return (item.config && item.config.startDate) || item.start_date;
        },
        endOf(item) {
            return (item.config && item.config.endDate) || item.end_date;
        },
        periodDescription(item) {
            const start = this.startOf(item);
            const end = this.endOf(item);
            if (!start && !end) return "Período não informado";
            return `${start ? this.formatDate(start) : "?"} até ${end ? this.formatDate(end) : "sem fim"}`;
        },
        periodPercent(item) {
            const start = moment.utc(this.startOf(item));
            const end = moment.utc(this.endOf(item));
            if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return 0;
            const today = moment.utc(moment().format("YYYY-MM-DD"));
            return Math.min(100, Math.max(0, today.diff(start, "days") / end.diff(start, "days") * 100));
        },
        periodProgress(item) {
            const start = moment.utc(this.startOf(item));
            const end = moment.utc(this.endOf(item));
            const today = moment.utc(moment().format("YYYY-MM-DD"));
            if (!start.isValid() || !end.isValid()) return "";
            if (today.isBefore(start, "day")) return `Começa em ${start.diff(today, "days")} dia(s)`;
            if (today.isAfter(end, "day")) return "Período concluído";
            const days = end.diff(today, "days");
            return days === 0 ? "Termina hoje" : `${days} dia(s) restante(s)`;
        },
        passengerNames(item) {
            const passengers = item.passengers || [];
            if (!passengers.length) return "Nenhum passageiro";
            const names = passengers.slice(0, 2).map(p => p.name);
            const rest = passengers.length - names.length;
            return `${names.join(", ")}${rest > 0 ? ` e mais ${rest}` : ""}`;
        },
        nextLabel(item) {
            if (item.status === 2) return "Pausada";
            if (item.status === 3) return "Encerrada";
            if (item.status === 4) return "Período finalizado";
            const next = (item.next_dates || [])[0];
            if (!next) return "Nenhuma data prevista";
            return next === moment().format("YYYY-MM-DD") ? "Hoje" : `${this.formatDate(next)} (${moment(next).format("ddd")})`;
        },
        runLabel(run) {
            const failed = Number(run.failed_count) || 0;
            return failed ? `${failed} falha(s) em ${this.formatDate(run.run_date)}` : `${run.created_count} criada(s) em ${this.formatDate(run.run_date)}`;
        },
        formatDate(value) {
            return value ? moment.utc(value).format("DD/MM/YYYY") : "-";
        },
        formatDay(value) {
            return moment(value).format("DD");
        },
        formatWeekday(value) {
            return moment(value).format("dddd");
        },
        formatMonth(value) {
            return moment(value).format("MMMM [de] YYYY");
        }
    }
};
</script>

<style lang="scss" scoped>
.status-filter {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
}

.status-filter__label {
    font-size: 13px;
    color: #5a6b85;
    margin-right: 10px;
}

.list-heading {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #5a6b85;
    margin-bottom: 10px;
}

.empty-card {
    padding: 32px;
    text-align: center;
    color: #5a6b85;
}

.rec-card {
    padding: 16px 18px 8px;
    margin-bottom: 14px;
    border-left: 4px solid #cfd8e3 !important;

    &--status-1 { border-left-color: #2e7d32 !important; }
    &--status-2 { border-left-color: #ef6c00 !important; }
    &--status-3 { border-left-color: #c62828 !important; }
    &--status-4 { border-left-color: #546e7a !important; }
}

.rec-card__header {
    display: flex;
    justify-content: space-between;
    gap: 12px;

    h3 {
        font-size: 17px;
        margin: 2px 0;
    }
}

.rec-card__eyebrow {
    font-size: 11px;
    letter-spacing: .06em;
    color: #8a96a8;
    font-weight: 700;
}

.rec-card__owner {
    font-size: 12px;
    color: #5a6b85;
}

.rec-card__today {
    font-size: 12px;
    color: #1450b5;
    font-weight: 600;
    margin-top: 4px;
}

.rec-card__alert {
    margin: 10px 0 0;
}

.rec-card__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 14px;
    margin: 14px 0 6px;
}

.rec-info {
    display: flex;
    flex-direction: column;

    small {
        font-size: 10.5px;
        letter-spacing: .06em;
        color: #8a96a8;
        font-weight: 700;
    }

    strong {
        font-size: 14px;
    }

    span {
        font-size: 12px;
        color: #5a6b85;
    }
}

.rec-stats {
    display: flex;
    gap: 10px;
}

.rec-card__footer {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    border-top: 1px solid #eef1f6;
    padding-top: 6px;
}

.details-body {
    min-height: 420px;
}

.details-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
    margin: 12px 0 18px;

    div {
        display: flex;
        flex-direction: column;
    }

    small {
        color: #8a96a8;
        font-size: 12px;
    }
}

.section-title {
    margin: 14px 0 8px;
    font-size: 15px;
}

.next-dates {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.next-date {
    display: flex;
    align-items: center;
    gap: 10px;
    border: 1px solid #e5e9f0;
    border-radius: 8px;
    padding: 8px 12px;

    span {
        font-size: 22px;
        font-weight: 700;
        color: #1450b5;
    }

    div {
        display: flex;
        flex-direction: column;
        text-transform: capitalize;
    }

    small {
        color: #8a96a8;
    }
}
</style>
